<template>
  <div class="space-y-6">
    <!-- 헤더와 파일 업로드 -->
    <div class="card-base p-6">
      <div class="page-header mb-6">
        <div>
          <h2 class="page-title">크레딧 기본정보</h2>
          <p class="page-subtitle">
            크레딧 채권 데이터를 관리하고 매일 업데이트합니다
          </p>
        </div>
      </div>

      <!-- 파일 업로드 영역 -->
      <div class="bg-gray-50 rounded-lg p-4 mb-6">
        <div class="flex items-center justify-between mb-4">
          <div>
            <h3 class="text-sm font-medium text-gray-900">
              크레딧 기본정보 업로드
            </h3>
            <p class="text-xs text-gray-500 mt-1">
              크레딧_YYYYMMDD.xlsx 형식의 파일을 업로드하세요
            </p>
          </div>
          <div class="text-xs text-gray-500">기준일: {{ selectedDate }}</div>
        </div>

        <div class="flex items-center space-x-3">
          <label class="btn-secondary cursor-pointer">
            <i class="fas fa-file-upload mr-2"></i>
            파일 선택
            <input type="file" class="hidden" accept=".xlsx,.xls" @change="handleFileSelect" />
          </label>

          <BaseButton variant="secondary" icon="fas fa-upload" :disabled="!selectedFile || isUploading"
            :loading="isUploading" @click="uploadFile">
            업로드
          </BaseButton>

          <BaseButton variant="outline" icon="fas fa-chart-line" :disabled="isAnalyzing" :loading="isAnalyzing"
            @click="analyzePriceData">
            가격정보 분석
          </BaseButton>

          <!-- 구분선 -->
          <div class="h-8 w-px bg-gray-300"></div>

          <BaseButton variant="primary" icon="fas fa-external-link-alt" @click="analyzeInfomaxData">
            인포맥스 API 분석
          </BaseButton>
        </div>

        <!-- 선택된 파일명 표시 -->
        <div v-if="selectedFileName"
          class="flex items-center space-x-2 mt-3 text-gray-600 bg-white px-3 py-2 rounded border text-sm">
          <i class="fas fa-file-excel text-green-600"></i>
          <span class="truncate">{{ selectedFileName }}</span>
          <button @click="clearFile" class="text-gray-400 hover:text-gray-600 ml-auto">
            <i class="fas fa-times"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- 크레딧 수급정보 업로드 -->
    <div class="card-base p-6">
      <div class="page-header mb-6">
        <div>
          <h2 class="page-title">크레딧 수급정보</h2>
          <p class="page-subtitle">
            크레딧 수급정보 데이터를 관리하고 매일 업데이트합니다
          </p>
        </div>
      </div>

      <!-- 유통시장 섹션 -->
      <div class="bg-gray-50 rounded-lg p-4 mb-4">
        <div class="flex items-center justify-between mb-4">
          <div>
            <h3 class="text-sm font-medium text-gray-900">유통시장</h3>
            <p class="text-xs text-gray-500 mt-1">
              매수/매도 파일을 업로드하세요
            </p>
          </div>
          <div class="text-xs text-gray-500">기준일: {{ selectedDate }}</div>
        </div>

        <div class="flex items-start justify-between">
          <div class="space-y-3">
            <!-- 매수 파일 -->
            <div class="bg-white rounded-lg p-3 border border-gray-200 inline-block">
              <div class="mb-2">
                <h4 class="text-sm font-medium text-gray-700">크레딧수급매수_YYYYMMDD.xlsx 업로드하세요</h4>
              </div>
              <div class="flex items-center space-x-3">
                <label class="btn-secondary cursor-pointer flex-shrink-0">
                  <i class="fas fa-file-upload mr-2"></i>
                  파일 선택
                  <input type="file" class="hidden" accept=".xlsx,.xls" @change="handleSupplyBuyFileSelect" />
                </label>

                <BaseButton variant="secondary" icon="fas fa-upload"
                  :disabled="!selectedSupplyBuyFile || isUploadingSupplyBuy" :loading="isUploadingSupplyBuy"
                  @click="uploadSupplyBuyFile">
                  업로드
                </BaseButton>

                <!-- 선택된 파일명 표시 -->
                <div v-if="selectedSupplyBuyFileName"
                  class="flex items-center space-x-2 text-gray-600 bg-gray-50 px-3 py-2 rounded border border-gray-200 text-sm">
                  <i class="fas fa-file-excel text-green-600"></i>
                  <span class="truncate">{{ selectedSupplyBuyFileName }}</span>
                  <button @click="clearSupplyBuyFile" class="text-gray-400 hover:text-gray-600 ml-2">
                    <i class="fas fa-times"></i>
                  </button>
                </div>
              </div>
            </div>

            <!-- 매도 파일 -->
            <div class="bg-white rounded-lg p-3 border border-gray-200 inline-block">
              <div class="mb-2">
                <h4 class="text-sm font-medium text-gray-700">크레딧수급매도_YYYYMMDD.xlsx 업로드하세요</h4>
              </div>
              <div class="flex items-center space-x-3">
                <label class="btn-secondary cursor-pointer flex-shrink-0">
                  <i class="fas fa-file-upload mr-2"></i>
                  파일 선택
                  <input type="file" class="hidden" accept=".xlsx,.xls" @change="handleSupplySellFileSelect" />
                </label>

                <BaseButton variant="secondary" icon="fas fa-upload"
                  :disabled="!selectedSupplySellFile || isUploadingSupplySell" :loading="isUploadingSupplySell"
                  @click="uploadSupplySellFile">
                  업로드
                </BaseButton>

                <!-- 선택된 파일명 표시 -->
                <div v-if="selectedSupplySellFileName"
                  class="flex items-center space-x-2 text-gray-600 bg-gray-50 px-3 py-2 rounded border border-gray-200 text-sm">
                  <i class="fas fa-file-excel text-green-600"></i>
                  <span class="truncate">{{ selectedSupplySellFileName }}</span>
                  <button @click="clearSupplySellFile" class="text-gray-400 hover:text-gray-600 ml-2">
                    <i class="fas fa-times"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- 분석 버튼 -->
          <div class="flex items-center">
            <BaseButton variant="outline" icon="fas fa-chart-line" :disabled="isAnalyzingSupplyMarket"
              :loading="isAnalyzingSupplyMarket" @click="analyzeSupplyMarketData">
              수급정보 분석
            </BaseButton>
          </div>
        </div>
      </div>

      <!-- 발행시장 섹션 -->
      <div class="bg-gray-50 rounded-lg p-4">
        <div class="flex items-center justify-between mb-4">
          <div>
            <h3 class="text-sm font-medium text-gray-900">발행시장</h3>
            <p class="text-xs text-gray-500 mt-1">
              크레딧수급발행_YYYYMMDD.xlsx 형식의 파일을 업로드하세요
            </p>
          </div>
          <div class="text-xs text-gray-500">기준일: {{ selectedDate }}</div>
        </div>

        <div class="flex items-center space-x-3">
          <label class="btn-secondary cursor-pointer">
            <i class="fas fa-file-upload mr-2"></i>
            파일 선택
            <input type="file" class="hidden" accept=".xlsx,.xls" @change="handleSupplyIssuanceFileSelect" />
          </label>

          <BaseButton variant="secondary" icon="fas fa-upload"
            :disabled="!selectedSupplyIssuanceFile || isUploadingSupplyIssuance" :loading="isUploadingSupplyIssuance"
            @click="uploadSupplyIssuanceFile">
            업로드
          </BaseButton>

          <!-- 구분선 -->
          <div class="h-8 w-px bg-gray-300"></div>

          <BaseButton variant="outline" icon="fas fa-chart-line" :disabled="isAnalyzingSupplyIssuance"
            :loading="isAnalyzingSupplyIssuance" @click="analyzeSupplyIssuanceData">
            수급정보 분석
          </BaseButton>
        </div>

        <!-- 선택된 파일명 표시 -->
        <div v-if="selectedSupplyIssuanceFileName"
          class="flex items-center space-x-2 mt-3 text-gray-600 bg-white px-3 py-2 rounded border text-sm">
          <i class="fas fa-file-excel text-green-600"></i>
          <span class="truncate">{{ selectedSupplyIssuanceFileName }}</span>
          <button @click="clearSupplyIssuanceFile" class="text-gray-400 hover:text-gray-600 ml-auto">
            <i class="fas fa-times"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- 크레딧 거래내역 업로드 -->
    <div class="card-base p-6">
      <div class="page-header mb-6">
        <div>
          <h2 class="page-title">크레딧 거래내역</h2>
          <p class="page-subtitle">
            크레딧 거래내역 데이터를 관리하고 매일 업데이트합니다
          </p>
        </div>
      </div>

      <!-- 거래내역 파일 업로드 영역 -->
      <div class="bg-gray-50 rounded-lg p-4 mb-6">
        <div class="flex items-center justify-between mb-4">
          <div>
            <h3 class="text-sm font-medium text-gray-900">
              크레딧 거래내역 업로드
            </h3>
            <p class="text-xs text-gray-500 mt-1">
              크레딧거래내역_YYYYMMDD.xlsx 형식의 파일을 업로드하세요
            </p>
          </div>
          <div class="text-xs text-gray-500">기준일: {{ selectedDate }}</div>
        </div>

        <div class="flex items-center space-x-3">
          <label class="btn-secondary cursor-pointer">
            <i class="fas fa-file-upload mr-2"></i>
            파일 선택
            <input type="file" class="hidden" accept=".xlsx,.xls" @change="handleTransactionFileSelect" />
          </label>

          <BaseButton variant="secondary" icon="fas fa-upload"
            :disabled="!selectedTransactionFile || isUploadingTransaction" :loading="isUploadingTransaction"
            @click="uploadTransactionFile">
            업로드
          </BaseButton>

          <BaseButton variant="outline" icon="fas fa-chart-line" :disabled="isAnalyzingTransaction"
            :loading="isAnalyzingTransaction" @click="analyzeTransactionData">
            거래내역 분석
          </BaseButton>

          <!-- 구분선 -->
          <div class="h-8 w-px bg-gray-300"></div>

          <BaseButton variant="primary" icon="fas fa-external-link-alt" :disabled="isRefreshingTransactionPrices"
            :loading="isRefreshingTransactionPrices" @click="refreshInfomaxTransactions">
            INFOMAX 거래내역 업데이트
          </BaseButton>
        </div>

        <!-- 선택된 파일명 표시 -->
        <div v-if="selectedTransactionFileName"
          class="flex items-center space-x-2 mt-3 text-gray-600 bg-white px-3 py-2 rounded border text-sm">
          <i class="fas fa-file-excel text-green-600"></i>
          <span class="truncate">{{ selectedTransactionFileName }}</span>
          <button @click="clearTransactionFile" class="text-gray-400 hover:text-gray-600 ml-auto">
            <i class="fas fa-times"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- 제외 키워드 영역 -->
    <div class="card-base p-6">
      <div class="mb-6">
        <h3 class="text-sm font-medium text-gray-900 mb-2">제외 키워드</h3>
        <p class="text-xs text-gray-500">분석에서 제외할 키워드를 관리합니다</p>
      </div>

      <div class="grid grid-cols-2 gap-6">
        <!-- 채권 분석 제외 키워드 -->
        <div class="space-y-4">
          <div class="flex items-center justify-between">
            <div>
              <h4 class="text-sm font-medium text-gray-900">채권 분석 제외 키워드</h4>
              <p class="text-xs text-gray-500 mt-1">채권 분석에서 제외할 키워드</p>
            </div>
            <BaseButton variant="primary" icon="fas fa-plus" size="sm" @click="showAddKeywordPrompt('BOND')"
              :disabled="isLoadingBondKeywords">
              키워드 추가
            </BaseButton>
          </div>

          <div v-if="isLoadingBondKeywords" class="flex items-center justify-center py-8">
            <div class="loading-spinner-sm"></div>
            <span class="ml-2 text-sm text-gray-500">키워드 로딩 중...</span>
          </div>

          <div v-else-if="bondExclusionKeywords.length > 0" class="space-y-2">
            <div v-for="(keyword, index) in sortedBondExclusionKeywords" :key="keyword.id" class="keyword-item group">
              <div
                class="flex items-center justify-between py-3 px-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <div class="flex items-center space-x-3">
                  <span class="keyword-number">{{ index + 1 }}</span>
                  <span class="keyword-text">{{ keyword.keyword }}</span>
                </div>
                <div class="flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button @click="editKeyword(keyword, 'BOND')" class="edit-btn" title="키워드 수정">
                    <i class="fas fa-edit"></i>
                  </button>
                  <button @click="deleteKeyword(keyword, 'BOND')" class="delete-btn" title="키워드 삭제">
                    <i class="fas fa-trash"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div v-else class="text-center py-8">
            <div class="text-gray-400 mb-2">
              <i class="fas fa-list-alt text-2xl"></i>
            </div>
            <p class="text-sm text-gray-500">등록된 제외 키워드가 없습니다.</p>
            <p class="text-xs text-gray-400 mt-1">키워드를 추가하여 채권 분석을 개선하세요.</p>
          </div>
        </div>

        <!-- 채팅 분석 제외 키워드 -->
        <div class="space-y-4">
          <div class="flex items-center justify-between">
            <div>
              <h4 class="text-sm font-medium text-gray-900">채팅 분석 제외 키워드</h4>
              <p class="text-xs text-gray-500 mt-1">채팅 분석에서 제외할 키워드</p>
            </div>
            <BaseButton variant="primary" icon="fas fa-plus" size="sm" @click="showAddKeywordPrompt('CHAT')"
              :disabled="isLoadingChatKeywords">
              키워드 추가
            </BaseButton>
          </div>

          <div v-if="isLoadingChatKeywords" class="flex items-center justify-center py-8">
            <div class="loading-spinner-sm"></div>
            <span class="ml-2 text-sm text-gray-500">키워드 로딩 중...</span>
          </div>

          <div v-else-if="chatExclusionKeywords.length > 0" class="space-y-2">
            <div v-for="(keyword, index) in sortedChatExclusionKeywords" :key="keyword.id" class="keyword-item group">
              <div
                class="flex items-center justify-between py-3 px-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <div class="flex items-center space-x-3">
                  <span class="keyword-number">{{ index + 1 }}</span>
                  <span class="keyword-text">{{ keyword.keyword }}</span>
                </div>
                <div class="flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button @click="editKeyword(keyword, 'CHAT')" class="edit-btn" title="키워드 수정">
                    <i class="fas fa-edit"></i>
                  </button>
                  <button @click="deleteKeyword(keyword, 'CHAT')" class="delete-btn" title="키워드 삭제">
                    <i class="fas fa-trash"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div v-else class="text-center py-8">
            <div class="text-gray-400 mb-2">
              <i class="fas fa-list-alt text-2xl"></i>
            </div>
            <p class="text-sm text-gray-500">등록된 제외 키워드가 없습니다.</p>
            <p class="text-xs text-gray-400 mt-1">키워드를 추가하여 채팅 분석을 개선하세요.</p>
          </div>
        </div>
      </div>
    </div>



    <!-- 로딩 오버레이 -->
    <div
      v-if="isUploading || isAnalyzing || isUploadingSupplyBuy || isUploadingSupplySell || isAnalyzingSupplyMarket || isUploadingSupplyIssuance || isAnalyzingSupplyIssuance || isUploadingTransaction || isAnalyzingTransaction || isRefreshingTransactionPrices"
      class="loading-overlay">
      <div class="bg-white rounded-xl p-8 shadow-2xl">
        <div class="flex items-center space-x-4">
          <div class="loading-spinner"></div>
          <span class="text-gray-700 font-medium">
            {{ getLoadingMessage() }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue';
import BaseButton from '@/components/common/BaseButton.vue';
import bondService from '@/service/bondService';
import transactionService from '@/service/transactionService';
import marketFlowService from '@/service/marketFlowService';

export default {
  name: 'CreditBasicComponent',
  components: {
    BaseButton,
  },
  props: {
    selectedDate: {
      type: String,
      required: true,
    },
  },

  setup(props) {
    // File upload states
    const selectedFile = ref(null);
    const selectedFileName = ref('');
    const isUploading = ref(false);
    const isAnalyzing = ref(false);
    // Props에서 받은 선택된 날짜 사용

    // Supply file upload states - 유통시장
    const selectedSupplyBuyFile = ref(null);
    const selectedSupplyBuyFileName = ref('');
    const isUploadingSupplyBuy = ref(false);
    const selectedSupplySellFile = ref(null);
    const selectedSupplySellFileName = ref('');
    const isUploadingSupplySell = ref(false);
    const isAnalyzingSupplyMarket = ref(false);

    // Supply file upload states - 발행시장
    const selectedSupplyIssuanceFile = ref(null);
    const selectedSupplyIssuanceFileName = ref('');
    const isUploadingSupplyIssuance = ref(false);
    const isAnalyzingSupplyIssuance = ref(false);

    // Transaction file upload states
    const selectedTransactionFile = ref(null);
    const selectedTransactionFileName = ref('');
    const isUploadingTransaction = ref(false);
    const isAnalyzingTransaction = ref(false);
    const isRefreshingTransactionPrices = ref(false);

    // Exclusion keywords states
    const exclusionKeywords = ref([]); // This will be used for BOND and CHAT keywords
    const isLoadingKeywords = ref(false);
    const hoveredKeywordId = ref(null);

    // Specific exclusion keyword states for BOND and CHAT
    const bondExclusionKeywords = ref([]);
    const chatExclusionKeywords = ref([]);
    const isLoadingBondKeywords = ref(false);
    const isLoadingChatKeywords = ref(false);

    // File upload handlers
    const handleFileSelect = event => {
      const file = event.target.files[0];
      if (!file) {
        clearFile();
        return;
      }

      const fileNamePattern = /^크레딧_\d{8}\.xlsx$/;
      const fileName = file.name.normalize('NFC');

      if (fileNamePattern.test(fileName)) {
        selectedFileName.value = fileName;
        selectedFile.value = file;
      } else {
        alert('파일명은 "크레딧_YYYYMMDD.xlsx" 형식이어야 합니다.');
        event.target.value = '';
        clearFile();
      }
    };

    const clearFile = () => {
      selectedFileName.value = '';
      selectedFile.value = null;
    };

    const uploadFile = async () => {
      if (!selectedFile.value) {
        alert('파일을 선택해주세요.');
        return;
      }

      isUploading.value = true;
      try {
        const formData = new FormData();
        formData.append('file', selectedFile.value);
        formData.append('fileName', selectedFile.value.name.normalize('NFC'));
        formData.append('date', props.selectedDate);
        formData.append('bondType', 'CREDIT');

        await bondService.uploadBondFile(props.selectedDate, selectedFile.value.name.normalize('NFC'), 'CREDIT', selectedFile.value);
        alert('파일이 성공적으로 업로드되었습니다.');
        clearFile();
      } catch (error) {
        console.error('파일 업로드 중 오류 발생:', error);
        alert('파일 업로드에 실패했습니다. 다시 시도해주세요.');
      } finally {
        isUploading.value = false;
      }
    };

    const analyzePriceData = async () => {
      isAnalyzing.value = true;
      try {
        await bondService.upsertBondOfDay(props.selectedDate, 'CREDIT');
        alert('가격정보 분석이 완료되었습니다.');
      } catch (error) {
        console.error('데이터 분석 중 오류 발생:', error);
        alert('데이터 분석에 실패했습니다. 다시 시도해주세요.');
      } finally {
        isAnalyzing.value = false;
      }
    };

    const analyzeInfomaxData = () => {
      alert('인포맥스 API 분석 기능을 준비 중입니다.');
    };

    // Supply file upload handlers - 유통시장 매수
    const handleSupplyBuyFileSelect = event => {
      const file = event.target.files[0];
      if (!file) {
        clearSupplyBuyFile();
        return;
      }

      const fileNamePattern = /^크레딧수급매수_\d{8}\.xlsx$/;
      const fileName = file.name.normalize('NFC');

      if (fileNamePattern.test(fileName)) {
        selectedSupplyBuyFileName.value = fileName;
        selectedSupplyBuyFile.value = file;
      } else {
        alert('파일명은 "크레딧수급매수_YYYYMMDD.xlsx" 형식이어야 합니다.');
        event.target.value = '';
        clearSupplyBuyFile();
      }
    };

    const clearSupplyBuyFile = () => {
      selectedSupplyBuyFileName.value = '';
      selectedSupplyBuyFile.value = null;
    };

    const uploadSupplyBuyFile = async () => {
      if (!selectedSupplyBuyFile.value) {
        alert('파일을 선택해주세요.');
        return;
      }

      isUploadingSupplyBuy.value = true;
      try {
        await marketFlowService.uploadSecondaryBuyFile(props.selectedDate, selectedSupplyBuyFile.value);
        alert('매수 파일이 성공적으로 업로드되었습니다.');
        clearSupplyBuyFile();
      } catch (error) {
        console.error('매수 파일 업로드 중 오류 발생:', error);
        alert('매수 파일 업로드에 실패했습니다. 다시 시도해주세요.');
      } finally {
        isUploadingSupplyBuy.value = false;
      }
    };

    // Supply file upload handlers - 유통시장 매도
    const handleSupplySellFileSelect = event => {
      const file = event.target.files[0];
      if (!file) {
        clearSupplySellFile();
        return;
      }

      const fileNamePattern = /^크레딧수급매도_\d{8}\.xlsx$/;
      const fileName = file.name.normalize('NFC');

      if (fileNamePattern.test(fileName)) {
        selectedSupplySellFileName.value = fileName;
        selectedSupplySellFile.value = file;
      } else {
        alert('파일명은 "크레딧수급매도_YYYYMMDD.xlsx" 형식이어야 합니다.');
        event.target.value = '';
        clearSupplySellFile();
      }
    };

    const clearSupplySellFile = () => {
      selectedSupplySellFileName.value = '';
      selectedSupplySellFile.value = null;
    };

    const uploadSupplySellFile = async () => {
      if (!selectedSupplySellFile.value) {
        alert('파일을 선택해주세요.');
        return;
      }

      isUploadingSupplySell.value = true;
      try {
        await marketFlowService.uploadSecondarySellFile(props.selectedDate, selectedSupplySellFile.value);
        alert('매도 파일이 성공적으로 업로드되었습니다.');
        clearSupplySellFile();
      } catch (error) {
        console.error('매도 파일 업로드 중 오류 발생:', error);
        alert('매도 파일 업로드에 실패했습니다. 다시 시도해주세요.');
      } finally {
        isUploadingSupplySell.value = false;
      }
    };

    // 유통시장 분석
    const analyzeSupplyMarketData = async () => {
      isAnalyzingSupplyMarket.value = true;
      try {
        await marketFlowService.analyzeSecondaryMarket(props.selectedDate);
        alert('유통시장 수급정보 분석이 완료되었습니다.');
      } catch (error) {
        console.error('유통시장 분석 중 오류 발생:', error);
        alert('유통시장 분석에 실패했습니다. 다시 시도해주세요.');
      } finally {
        isAnalyzingSupplyMarket.value = false;
      }
    };

    // Supply file upload handlers - 발행시장
    const handleSupplyIssuanceFileSelect = event => {
      const file = event.target.files[0];
      if (!file) {
        clearSupplyIssuanceFile();
        return;
      }

      const fileNamePattern = /^크레딧수급발행_\d{8}\.xlsx$/;
      const fileName = file.name.normalize('NFC');

      if (fileNamePattern.test(fileName)) {
        selectedSupplyIssuanceFileName.value = fileName;
        selectedSupplyIssuanceFile.value = file;
      } else {
        alert('파일명은 "크레딧수급발행_YYYYMMDD.xlsx" 형식이어야 합니다.');
        event.target.value = '';
        clearSupplyIssuanceFile();
      }
    };

    const clearSupplyIssuanceFile = () => {
      selectedSupplyIssuanceFileName.value = '';
      selectedSupplyIssuanceFile.value = null;
    };

    const uploadSupplyIssuanceFile = async () => {
      if (!selectedSupplyIssuanceFile.value) {
        alert('파일을 선택해주세요.');
        return;
      }

      isUploadingSupplyIssuance.value = true;
      try {
        await marketFlowService.uploadPrimaryIssuanceFile(props.selectedDate, selectedSupplyIssuanceFile.value);
        alert('발행시장 파일이 성공적으로 업로드되었습니다.');
        clearSupplyIssuanceFile();
      } catch (error) {
        console.error('발행시장 파일 업로드 중 오류 발생:', error);
        alert('발행시장 파일 업로드에 실패했습니다. 다시 시도해주세요.');
      } finally {
        isUploadingSupplyIssuance.value = false;
      }
    };

    // 발행시장 분석
    const analyzeSupplyIssuanceData = async () => {
      isAnalyzingSupplyIssuance.value = true;
      try {
        await marketFlowService.analyzePrimaryMarket(props.selectedDate);
        alert('발행시장 수급정보 분석이 완료되었습니다.');
      } catch (error) {
        console.error('발행시장 분석 중 오류 발생:', error);
        alert('발행시장 분석에 실패했습니다. 다시 시도해주세요.');
      } finally {
        isAnalyzingSupplyIssuance.value = false;
      }
    };

    // Transaction file upload handlers
    const handleTransactionFileSelect = event => {
      const file = event.target.files[0];
      if (!file) {
        clearTransactionFile();
        return;
      }

      const fileNamePattern = /^크레딧거래내역_\d{8}\.xlsx$/;
      const fileName = file.name.normalize('NFC');

      if (fileNamePattern.test(fileName)) {
        selectedTransactionFileName.value = fileName;
        selectedTransactionFile.value = file;
      } else {
        alert('파일명은 "크레딧거래내역_YYYYMMDD.xlsx" 형식이어야 합니다.');
        event.target.value = '';
        clearTransactionFile();
      }
    };

    const clearTransactionFile = () => {
      selectedTransactionFileName.value = '';
      selectedTransactionFile.value = null;
    };

    const uploadTransactionFile = async () => {
      if (!selectedTransactionFile.value) {
        alert('파일을 선택해주세요.');
        return;
      }

      isUploadingTransaction.value = true;
      try {
        await transactionService.uploadTransactionFile(
          props.selectedDate,
          selectedTransactionFile.value.name.normalize('NFC'),
          'CREDIT',
          selectedTransactionFile.value
        );
        alert('거래내역 파일이 성공적으로 업로드되었습니다.');
        clearTransactionFile();
      } catch (error) {
        console.error('거래내역 파일 업로드 중 오류 발생:', error);
        alert('거래내역 파일 업로드에 실패했습니다. 다시 시도해주세요.');
      } finally {
        isUploadingTransaction.value = false;
      }
    };

    const analyzeTransactionData = async () => {
      isAnalyzingTransaction.value = true;
      try {
        const result = await transactionService.refreshTransactionFromExcel(props.selectedDate, 'CREDIT');
        alert(`거래내역 분석이 완료되었습니다. (처리된 건수: ${result || 0}건)`);
      } catch (error) {
        console.error('거래내역 분석 중 오류 발생:', error);
        alert('거래내역 분석에 실패했습니다. 다시 시도해주세요.');
      } finally {
        isAnalyzingTransaction.value = false;
      }
    };

    const refreshInfomaxTransactions = async () => {
      if (!confirm('INFOMAX에서 거래내역 정보를 일괄 업데이트합니다.\n이 작업은 시간이 오래 걸릴 수 있습니다.(최대 1~2분 소요)')) {
        return;
      }

      isRefreshingTransactionPrices.value = true;
      try {
        const result = await transactionService.refreshTransactions(props.selectedDate, 'CREDIT');
        const count = result || 0;
        alert(`거래내역 일괄 업데이트가 완료되었습니다. (${count}건)`);
      } catch (error) {
        console.error('INFOMAX 거래내역 업데이트 중 오류 발생:', error);

        if (error.errorCode === 'ACCESS_LIMIT_EXCEEDED') {
          alert('INFOMAX API 호출 한도를 초과했습니다.\n잠시 후 다시 시도해주세요.');
        } else {
          alert('INFOMAX 거래내역 일괄 업데이트에 실패했습니다. 다시 시도해주세요.');
        }
      } finally {
        isRefreshingTransactionPrices.value = false;
      }
    };

    // Loading message helper
    const getLoadingMessage = () => {
      if (isUploading.value) return '기본정보 파일을 업로드하는 중...';
      if (isAnalyzing.value) return '가격정보 분석 중...';
      if (isUploadingSupplyBuy.value) return '매수 파일을 업로드하는 중...';
      if (isUploadingSupplySell.value) return '매도 파일을 업로드하는 중...';
      if (isAnalyzingSupplyMarket.value) return '유통시장 수급정보 분석 중...';
      if (isUploadingSupplyIssuance.value) return '발행시장 파일을 업로드하는 중...';
      if (isAnalyzingSupplyIssuance.value) return '발행시장 수급정보 분석 중...';
      if (isUploadingTransaction.value) return '거래내역 파일을 업로드하는 중...';
      if (isAnalyzingTransaction.value) return '거래내역 분석 중...';
      if (isRefreshingTransactionPrices.value) return 'INFOMAX 거래내역 업데이트 중...';
      return '처리 중...';
    };

    // Computed properties
    const sortedExclusionKeywords = computed(() => {
      return [...exclusionKeywords.value].sort((a, b) => {
        return new Date(b.createdDateTime) - new Date(a.createdDateTime);
      });
    });

    const sortedBondExclusionKeywords = computed(() => {
      return [...bondExclusionKeywords.value].sort((a, b) => {
        return new Date(b.createdDateTime) - new Date(a.createdDateTime);
      });
    });

    const sortedChatExclusionKeywords = computed(() => {
      return [...chatExclusionKeywords.value].sort((a, b) => {
        return new Date(b.createdDateTime) - new Date(a.createdDateTime);
      });
    });

    // Exclusion keywords methods
    const loadExclusionKeywords = async () => {
      isLoadingKeywords.value = true;
      try {
        const response = await bondService.getExclusionKeywords('BOND');
        exclusionKeywords.value = response || [];
      } catch (error) {
        console.error('제외 키워드 조회 중 오류 발생:', error);
        exclusionKeywords.value = [];
      } finally {
        isLoadingKeywords.value = false;
      }
    };

    const loadBondExclusionKeywords = async () => {
      isLoadingBondKeywords.value = true;
      try {
        const response = await bondService.getExclusionKeywords('BOND');
        bondExclusionKeywords.value = response || [];
      } catch (error) {
        console.error('채권 제외 키워드 조회 중 오류 발생:', error);
        bondExclusionKeywords.value = [];
      } finally {
        isLoadingBondKeywords.value = false;
      }
    };

    const loadChatExclusionKeywords = async () => {
      isLoadingChatKeywords.value = true;
      try {
        const response = await bondService.getExclusionKeywords('CHAT');
        chatExclusionKeywords.value = response || [];
      } catch (error) {
        console.error('채팅 제외 키워드 조회 중 오류 발생:', error);
        chatExclusionKeywords.value = [];
      } finally {
        isLoadingChatKeywords.value = false;
      }
    };

    const showAddKeywordPrompt = async (type) => {
      const keyword = prompt(`${type === 'BOND' ? '채권' : '채팅'} 분석에서 제외할 키워드를 입력하세요:`);
      if (!keyword || !keyword.trim()) return;

      const confirmed = confirm(
        `${keyword.trim()} 키워드를 ${type === 'BOND' ? '채권' : '채팅'} 분석에서 제외하시겠습니까?\n\n⚠️ 추가한 키워드는 즉시 반영되지 않고, 다음 분석때 적용됩니다.`
      );

      if (!confirmed) return;

      try {
        const response = await bondService.createExclusionKeyword(keyword.trim(), type);
        if (type === 'BOND') {
          bondExclusionKeywords.value.push(response);
        } else {
          chatExclusionKeywords.value.push(response);
        }
        alert('키워드가 성공적으로 추가되었습니다.');
      } catch (error) {
        console.error('키워드 추가 중 오류 발생:', error);
        alert('키워드 추가에 실패했습니다. 다시 시도해주세요.');
      }
    };

    const editKeyword = async (keyword, type) => {
      const newKeyword = prompt(`${type === 'BOND' ? '채권' : '채팅'} 분석에서 "${keyword.keyword}"를 수정하세요:`, keyword.keyword);
      if (!newKeyword || !newKeyword.trim()) return;

      if (newKeyword.trim() === keyword.keyword) {
        alert('기존 키워드와 동일합니다.');
        return;
      }

      const confirmed = confirm(
        `${keyword.keyword} → "${newKeyword.trim()}"로 수정하시겠습니까?\n\n⚠️ 수정한 키워드는 즉시 반영되지 않고, 다음 분석때 적용됩니다.`
      );

      if (!confirmed) return;

      try {
        // 기존 키워드 삭제 후 새 키워드 추가
        await bondService.deleteExclusionKeyword(keyword.id);
        const response = await bondService.createExclusionKeyword(newKeyword.trim(), type);

        // 로컬 상태 업데이트
        const index = exclusionKeywords.value.findIndex(k => k.id === keyword.id);
        if (index !== -1) {
          exclusionKeywords.value[index] = response;
        }

        if (type === 'BOND') {
          const bondIndex = bondExclusionKeywords.value.findIndex(k => k.id === keyword.id);
          if (bondIndex !== -1) {
            bondExclusionKeywords.value[bondIndex] = response;
          }
        } else {
          const chatIndex = chatExclusionKeywords.value.findIndex(k => k.id === keyword.id);
          if (chatIndex !== -1) {
            chatExclusionKeywords.value[chatIndex] = response;
          }
        }

        alert('키워드가 성공적으로 수정되었습니다.');
      } catch (error) {
        console.error('키워드 수정 중 오류 발생:', error);
        alert('키워드 수정에 실패했습니다. 다시 시도해주세요.');
      }
    };

    const deleteKeyword = async (keyword, type) => {
      if (!confirm(`"${keyword.keyword}" 키워드를 ${type === 'BOND' ? '채권' : '채팅'} 분석에서 제외하시겠습니까?`)) {
        return;
      }

      try {
        await bondService.deleteExclusionKeyword(keyword.id);
        if (type === 'BOND') {
          bondExclusionKeywords.value = bondExclusionKeywords.value.filter(k => k.id !== keyword.id);
        } else {
          chatExclusionKeywords.value = chatExclusionKeywords.value.filter(k => k.id !== keyword.id);
        }
        alert('키워드가 성공적으로 삭제되었습니다.');
      } catch (error) {
        console.error('키워드 삭제 중 오류 발생:', error);
        alert('키워드 삭제에 실패했습니다. 다시 시도해주세요.');
      }
    };

    // Load exclusion keywords on component mount
    onMounted(() => {
      loadExclusionKeywords();
      loadBondExclusionKeywords();
      loadChatExclusionKeywords();
    });

    return {
      // File upload
      selectedFile,
      selectedFileName,
      isUploading,
      isAnalyzing,

      // Supply file upload - 유통시장
      selectedSupplyBuyFile,
      selectedSupplyBuyFileName,
      isUploadingSupplyBuy,
      selectedSupplySellFile,
      selectedSupplySellFileName,
      isUploadingSupplySell,
      isAnalyzingSupplyMarket,

      // Supply file upload - 발행시장
      selectedSupplyIssuanceFile,
      selectedSupplyIssuanceFileName,
      isUploadingSupplyIssuance,
      isAnalyzingSupplyIssuance,

      // Transaction file upload
      selectedTransactionFile,
      selectedTransactionFileName,
      isUploadingTransaction,
      isAnalyzingTransaction,
      isRefreshingTransactionPrices,

      // Exclusion keywords
      exclusionKeywords,
      isLoadingKeywords,
      hoveredKeywordId,
      sortedExclusionKeywords,
      bondExclusionKeywords,
      chatExclusionKeywords,
      isLoadingBondKeywords,
      isLoadingChatKeywords,
      sortedBondExclusionKeywords,
      sortedChatExclusionKeywords,

      // Methods
      handleFileSelect,
      clearFile,
      uploadFile,
      analyzePriceData,
      analyzeInfomaxData,
      handleSupplyBuyFileSelect,
      clearSupplyBuyFile,
      uploadSupplyBuyFile,
      handleSupplySellFileSelect,
      clearSupplySellFile,
      uploadSupplySellFile,
      analyzeSupplyMarketData,
      handleSupplyIssuanceFileSelect,
      clearSupplyIssuanceFile,
      uploadSupplyIssuanceFile,
      analyzeSupplyIssuanceData,
      handleTransactionFileSelect,
      clearTransactionFile,
      uploadTransactionFile,
      analyzeTransactionData,
      refreshInfomaxTransactions,
      getLoadingMessage,
      loadExclusionKeywords,
      loadBondExclusionKeywords,
      loadChatExclusionKeywords,
      showAddKeywordPrompt,
      editKeyword,
      deleteKeyword,
    };
  },
};
</script>

<style scoped>
.btn-secondary {
  @apply inline-flex items-center justify-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed;
}

.btn-secondary:focus {
  @apply outline-none ring-2 ring-offset-2 ring-gray-500;
}

.loading-spinner-sm {
  @apply inline-block w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin;
}

/* 키워드 관련 스타일 */
.keyword-item {
  @apply border border-gray-200 rounded-lg hover:border-gray-300 transition-all duration-200;
}

.keyword-number {
  @apply inline-flex items-center justify-center w-6 h-6 bg-blue-100 text-blue-600 text-xs font-semibold rounded-full;
}

.keyword-text {
  @apply text-sm font-medium text-gray-900;
}

.edit-btn {
  @apply w-8 h-8 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center text-xs hover:bg-blue-200 transition-colors;
}

.delete-btn {
  @apply w-8 h-8 bg-red-100 text-red-600 rounded-lg flex items-center justify-center text-xs hover:bg-red-200 transition-colors;
}
</style>
